import React, { useLayoutEffect, useRef, useState } from 'react';
import overviewStyles from 'app/components/SlideOutPanel/Overview/Overview.module.scss';

// Matches Overview.tsx's own `MAX_COLLAPSED_HEIGHT` constant exactly - the
// same collapsed height its own Read More/Read Less fields use.
const MAX_COLLAPSED_HEIGHT = 288;

interface ReadMoreContentProps {
  /** The actual content being measured/displayed, as a plain re-measure
   * key - not rendered directly (`children` is) - so a Save that changes
   * the underlying HTML re-measures against the new content instead of a
   * stale height from before the edit. */
  content: string;
  children: React.ReactNode;
}

/**
 * Read More/Read Less for long content - reuses the Overview section's own
 * mechanism and styling exactly (Overview.tsx's `MAX_COLLAPSED_HEIGHT`,
 * and Overview.module.scss's `.fieldContent`/`.collapsed` clamp-with-fade
 * and `.readMoreButton` toggle), rather than a second implementation.
 * Overview's own version manages many named fields at once (a `Map`/`Set`
 * keyed by field name, plus scroll-restoration tied to its own hardcoded
 * `[data-testid="slideout-content-container"]` selector) because it has
 * many fields on screen simultaneously; this feature only ever has one
 * piece of content per card, so this is the same clamp-and-toggle
 * mechanism scoped to a single ref/boolean instead of that multi-field
 * map - not a different behavior, just not carrying over machinery this
 * feature has no equivalent for (its own, separate scroll container).
 */
export function ReadMoreContent({ content, children }: ReadMoreContentProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsReadMore, setNeedsReadMore] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const measure = () => {
      if (contentRef.current) {
        setNeedsReadMore(
          contentRef.current.scrollHeight > MAX_COLLAPSED_HEIGHT
        );
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [content]);

  const isCollapsed = !isExpanded && needsReadMore;

  return (
    <>
      <div
        ref={contentRef}
        className={
          isCollapsed
            ? `${overviewStyles.fieldContent} ${overviewStyles.collapsed}`
            : overviewStyles.fieldContent
        }
        style={{
          maxHeight: isCollapsed ? `${MAX_COLLAPSED_HEIGHT}px` : 'none',
          overflow: 'hidden',
          width: '100%'
        }}
      >
        {children}
      </div>
      {needsReadMore && (
        <button
          type="button"
          className={overviewStyles.readMoreButton}
          onClick={e => {
            e.stopPropagation();
            setIsExpanded(prev => !prev);
          }}
          aria-label={isExpanded ? 'Read Less' : 'Read More'}
        >
          {isExpanded ? 'Read Less' : 'Read More'}
        </button>
      )}
    </>
  );
}

export default ReadMoreContent;
